import React, {useCallback} from "react";
import {
  Box,
  Divider,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  useTheme,
} from "@mui/material";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import AssignmentLateOutlinedIcon from "@mui/icons-material/AssignmentLateOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DynamicFeedOutlinedIcon from "@mui/icons-material/DynamicFeedOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import FitnessCenterOutlinedIcon from "@mui/icons-material/FitnessCenterOutlined";
import FolderSpecialOutlinedIcon from "@mui/icons-material/FolderSpecialOutlined";
import GppMaybeOutlinedIcon from "@mui/icons-material/GppMaybeOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import LibraryBooksOutlinedIcon from "@mui/icons-material/LibraryBooksOutlined";
import ListAltIcon from "@mui/icons-material/ListAlt";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ManageSearchOutlinedIcon from "@mui/icons-material/ManageSearchOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import PartyModeOutlinedIcon from "@mui/icons-material/PartyModeOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import PieChartOutlinedIcon from "@mui/icons-material/PieChartOutlined";
import PolicyOutlinedIcon from "@mui/icons-material/PolicyOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import SettingsInputAntennaOutlinedIcon from "@mui/icons-material/SettingsInputAntennaOutlined";
import SettingsInputComponentOutlinedIcon from "@mui/icons-material/SettingsInputComponentOutlined";
import SettingsInputHdmiOutlinedIcon from "@mui/icons-material/SettingsInputHdmiOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import TextSnippetOutlinedIcon from "@mui/icons-material/TextSnippetOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import GettingStarted from "../topbar/GettingStarted";
import SidebarCreateButton from "./SidebarCreateButton";
import SidebarEntry from "./SidebarEntry";
import SidebarMenu from "./SidebarMenu";
import SidebarLogo from "./SidebarLogo";
import SidebarProfile from "./SidebarProfile";
import NotificationDrawer from "../notificationCenter/NotificationDrawer";
import SidebarIcon from "./SidebarIcon";
import StyledBadge from "../badges/StyledBadge";
import {useSidebarLayout, useSidebarData, useSidebarEphemeral} from "./Context/SidebarProvider";
import {usePermissions} from "../../context/AuthzProvider";
import {useFlag} from "@openfeature/react-sdk";
import {useFeatureFlags} from "../../context/FeatureFlagProvider";
import {useOrgSettings} from "../../context/OrgSettingsProvider";
import {compact, filter, isEmpty, map, some} from "underscore";
import {useSidebarContentStyles, useSidebarItemStyles} from "./sidebarStyles";

export const SidebarContent = () => {
  const {
    canAccessExercise,
    canAccessExerciseOrIncident,
    canAccessIncident,
    canAccessMessage,
    canAccessAuditSummary,
    canAccessDocumentLibrary,
    canAccessTrustedAdvisor,
    canManageApiKey,
    canManageDataOption,
    canManageDataset,
    canManageIntegration,
    canManageNotification,
    canManageObligation,
    canManageOrganization,
    canManagePermission,
    canManagePlaybook,
    canManageReportTemplate,
    canManageRole,
    canManageTask,
    canManageUser,
    canReadDashboard,
    canReadIncidentAuditLog,
    canReadIncidentSummary,
    canReadNews,
    canReadResponsePlan,
    canReadRiskAssessment,
  } = usePermissions();
  const {
    isSidebarExpanded,
    isSidebarTextVisible,
    currentSidebarWidth,
    isSidebarAnimating,
  } = useSidebarLayout();
  const {
    myActionCount,
    unreadData,
    unseenNotificationCounts,
    resetUnseenCounts,
  } = useSidebarData();
  const {notificationDrawerOpen, setNotificationDrawerOpen} = useSidebarEphemeral();
  const {
    responseWorkflowName,
    aiPermissions,
    notificationCenterEnabled,
    showRegulatoryResearch,
  } = useOrgSettings();
  const {showDeveloper, showGettingStarted} = useFeatureFlags();
  const {value: enableCustomNotificationTemplatesUi} =
    useFlag("enableCustomNotificationTemplatesUi", false);

  const theme = useTheme();
  const closeNotificationDrawer = useCallback(() => setNotificationDrawerOpen(false), []);
  const {classes} = useSidebarContentStyles({isSidebarExpanded});
  const {classes: itemClasses} = useSidebarItemStyles({
    isCurrentPage: false,
    inOverlay: isSidebarTextVisible,
  });

  const totalUnseen = Object.values(unseenNotificationCounts).reduce((a, b) => a + b, 0);

  const Separator = ({text}) => {
    return (
      <Grid container justifyContent="center" className={classes.container}>
        <Box p={1}>
          <Typography variant="caption">{text}</Typography>
        </Box>
      </Grid>
    );
  };

  const config = [
    {
      text: "Dashboard",
      to: "/dashboard",
      icon: DashboardOutlinedIcon,
      hide: !canReadDashboard || !canAccessExerciseOrIncident,
    },
    {
      text: "Respond",
      icon: GppMaybeOutlinedIcon,
      dataTour: "nav-top-level-items",
      flatOnNew: true,
      items: [
        {
          text: "My Actions",
          to: "/actions/list",
          icon: ListAltIcon,
          badge: myActionCount?.actions.length,
          hide: !canAccessExerciseOrIncident,
        },
        {
          text: `${responseWorkflowName.upper}s`,
          to: `/${responseWorkflowName.lower}s/list`,
          icon: AssignmentLateOutlinedIcon,
          hide: !canAccessIncident,
        },
        {
          text: "Exercises",
          to: "/exercises/list",
          icon: FitnessCenterOutlinedIcon,
          hide: !canAccessExercise,
        },
        {
          text: "Reports",
          to: "/reports",
          icon: DescriptionOutlinedIcon,
          hideOnNew: true,
          hide: !canAccessExerciseOrIncident,
        },
        {
          text: "Pending Evidence",
          to: "/evidence/pending",
          icon: Inventory2OutlinedIcon,
          hideOnNew: true,
          hide: !showDeveloper || !canAccessExerciseOrIncident,
        },
      ],
    },
    {
      text: "Compliance",
      icon: AssignmentTurnedInOutlinedIcon,
      items: [
        {
          text: "Audit Summary",
          to: "/compliance/summary",
          icon: FolderSpecialOutlinedIcon,
          hide: !canAccessAuditSummary,
        },
        {
          text: "Regulatory Research",
          to: "/compliance/research",
          icon: ManageSearchOutlinedIcon,
          hide: !showRegulatoryResearch,
        },
        {
          text: "Response Plan",
          to: "/compliance/plan",
          icon: PolicyOutlinedIcon,
          hide: !canReadResponsePlan,
        },
        {
          text: "Document Library",
          to: "/library",
          icon: MenuBookOutlinedIcon,
          hide: !canAccessDocumentLibrary,
        },
      ],
    },
    {
      text: "Reports",
      icon: AssessmentOutlinedIcon,
      dataTour: "nav-reports-menu",
      items: [
        {
          text: `${responseWorkflowName.upper} Reports`,
          to: "/reports",
          icon: DescriptionOutlinedIcon,
          dataTour: "nav-incident-reports",
          hideOnOld: true,
          hide: !canAccessExerciseOrIncident,
        },
        {
          text: "Risk Assessment",
          to: "/reports/assessment",
          icon: ErrorOutlineOutlinedIcon,
          hide: !canReadRiskAssessment,
        },
        {
          text: `${responseWorkflowName.upper}s Summary`,
          to: "/reports/summary",
          icon: PieChartOutlinedIcon,
          hide: !canReadIncidentSummary || !canAccessExerciseOrIncident,
        },
        {
          text: `${responseWorkflowName.upper}s Audit Log`,
          to: "/reports/auditlog",
          icon: AccessTimeOutlinedIcon,
          hide: !canReadIncidentAuditLog || !canAccessExerciseOrIncident,
        },
      ],
    },
    {
      text: "Maestro",
      to: "/rex-ai-maestro",
      icon: SmartToyOutlinedIcon,
      hide: !aiPermissions?.trustedAdvisor || !canAccessTrustedAdvisor,
    },
    {
      text: "Mail",
      to: "/messaging/list",
      icon: MailOutlineIcon,
      badge: unreadData?.userMessages.length,
      hide: !canAccessMessage,
    },
    {
      text: "Playground",
      to: "/playground",
      icon: PartyModeOutlinedIcon,
      hide: !showDeveloper,
    },
    {
      text: "News",
      to: "/news",
      icon: MenuBookOutlinedIcon,
      hide: !canReadNews,
    },
    {
      text: "Admin",
      separator: Separator,
      items: [
        {
          text: "Sources",
          icon: LayersOutlinedIcon,
          items: [
            {
              text: "Task Library",
              to: "/tasks/list",
              icon: AssignmentOutlinedIcon,
              hide: !canManageTask,
            },
            {
              text: "Procedures",
              to: "/procedures/list",
              icon: PublicOutlinedIcon,
              hide: !canManageObligation,
            },
            {
              text: "Playbooks",
              to: "/playbooks/list",
              icon: LibraryBooksOutlinedIcon,
              hide: !canManagePlaybook,
            },
            {
              text: "Report Templates",
              to: "/reportTemplates/list",
              icon: TextSnippetOutlinedIcon,
              hide: !canManageReportTemplate,
            },
            {
              text: "Datasets",
              to: "/datasets/list",
              icon: StorageOutlinedIcon,
              hide: !canManageDataset,
            },
            {
              text: "Data Options",
              to: "/dataOptions/conditions/list",
              icon: DynamicFeedOutlinedIcon,
              hide: !canManageDataOption,
            },
          ],
        },
        {
          text: "Settings",
          icon: SettingsOutlinedIcon,
          items: [
            {
              text: "Users",
              to: "/users/list",
              icon: PersonOutlinedIcon,
              hide: !canManageUser,
            },
            {
              text: "Roles",
              to: "/roles/list",
              icon: PeopleAltOutlinedIcon,
              hide: !canManageRole,
            },
            {
              text: "Permissions",
              to: "/permissions",
              icon: LockOutlinedIcon,
              hide: !canManagePermission,
            },
            {
              text: "Organization",
              to: "/organization/general",
              icon: BusinessOutlinedIcon,
              hide: !canManageOrganization,
            },
            {
              text: "Notifications",
              to: "/notifications",
              icon: SettingsInputAntennaOutlinedIcon,
              hide: !canManageNotification,
            },
            {
              text: "Notification Templates",
              to: "/notificationTemplates/list",
              icon: MarkEmailReadOutlinedIcon,
              hide: !canManageNotification || !enableCustomNotificationTemplatesUi,
            },
          ],
        },
        {
          text: "Technologies",
          icon: SettingsInputHdmiOutlinedIcon,
          items: [
            {
              text: "Integrations",
              to: "/integrations",
              icon: SettingsInputComponentOutlinedIcon,
              hide: !canManageIntegration,
            },
            {
              text: "API Keys",
              to: "/api/list",
              icon: VpnKeyOutlinedIcon,
              hide: !canManageApiKey,
            },
          ],
        },
      ],
    },
  ];

  const notHidden = (old) => (item) => {
    const visible = !item.hide && ((!item.hideOnOld && old) || (!item.hideOnNew && !old));

    if (visible && item.items) {
      return some(item.items, notHidden(old));
    }

    return visible;
  };

  const filterItems = (items, old) => filter(items, notHidden(old));

  const buildItem = ({icon: Icon, hide, hideOnOld, hideOnNew, dataTour, ...props}, old) => (
    <SidebarEntry
      {...props}
      key={props.text}
      dataTour={old ? undefined : dataTour}
      icon={<Icon />}
    />
  );

  const buildSeparator = ({separator: Separator, items, text}, old) => {
    const filteredItems = filterItems(items, old);

    if (isEmpty(filteredItems)) {
      return null;
    }

    return (
      <React.Fragment key={text}>
        <Separator text={text} />
        {buildMenu(items, old)}
      </React.Fragment>
    );
  };

  const buildSubMenu = (
    {items, icon: Icon, hide, hideOnOld, hideOnNew, flatOnNew, dataTour, ...props},
    old,
  ) => {
    const menuItems = map(filterItems(items, old), (item) => buildItem(item, old));

    if (isEmpty(menuItems)) {
      return null;
    }

    const menu =
      !old && flatOnNew ?
        (
          <React.Fragment key={props.text}>{menuItems}</React.Fragment>
        ) :
        (
          <SidebarMenu {...props} icon={<Icon />} key={props.text}>
            {menuItems}
          </SidebarMenu>
        );

    return !old && dataTour ?
      (
        <div key={props.text} data-tour={dataTour}>
          {menu}
        </div>
      ) :
      (
        menu
      );
  };

  const buildMenu = (config, old = false) =>
    compact(
      map(filterItems(config, old), (c) => {
        if (c.separator) {
          return buildSeparator(c, old);
        }

        if (c.items) {
          return buildSubMenu(c, old);
        }

        if (c.component) {
          const {component: Component, text} = c;
          return <Component key={text} />;
        }

        return buildItem(c, old);
      }),
    );

  const mainMenuConfig = config.slice(0, -1);
  const adminMenuConfig = config.slice(-1);

  return (
    <>
      <SidebarLogo />
      <SidebarCreateButton />
      <List className={classes.list}>
        {buildMenu(mainMenuConfig)}
        {showGettingStarted && (
          <GettingStarted popperPlacement="right" collapsedIcon={<RocketLaunchOutlinedIcon />} />
        )}
      </List>

      <Box flex={1} />

      <List className={classes.list}>{buildMenu(adminMenuConfig)}</List>

      <Divider sx={{mx: 2, borderColor: theme.palette.layout.sidebar.sidebarBorder}} />

      {notificationCenterEnabled && (
        <>
          <List className={classes.list}>
            <ListItemButton
              onClick={() => setNotificationDrawerOpen(!notificationDrawerOpen)}
              id="sidebar-Notification Center"
              data-testid="notification-center-toggle"
              className={itemClasses.item}
              disableRipple
            >
              <StyledBadge badgeContent={isSidebarTextVisible ? 0 : totalUnseen} max={999}>
                <SidebarIcon icon={<NotificationsNoneOutlinedIcon />} />
              </StyledBadge>
              <StyledBadge
                badgeContent={isSidebarTextVisible ? totalUnseen : 0}
                max={999}
                padParent
              >
                <ListItemText
                  primary="Notification Center"
                  className={
                    isSidebarTextVisible ? itemClasses.overlayItemText : itemClasses.itemText
                  }
                  primaryTypographyProps={{variant: "body2"}}
                />
              </StyledBadge>
            </ListItemButton>
          </List>

          <NotificationDrawer
            open={notificationDrawerOpen}
            isSidebarAnimating={isSidebarAnimating}
            onClose={closeNotificationDrawer}
            sidebarWidth={currentSidebarWidth}
            resetUnseenCounts={resetUnseenCounts}
          />
        </>
      )}

      <Box mb={1}>
        <SidebarProfile />
      </Box>
    </>
  );
};
